import { FiEdit } from 'react-icons/fi';

export const sentenceCompletionReadingData = {
  courseId: 'sentence-completion-reading',
  courseType: 'reading-course',
  title: 'Sentence Completion',
  icon: FiEdit,
  overview: {
    impact: 'Complete sentence stems accurately using evidence from the passage',
    focus: 'Detail location, grammar fit, and exact-form extraction',
    uses: 'Reading, precision, paraphrase handling',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'What This Question Type Measures',
      content:
        '<p>Sentence completion checks whether you can locate a specific detail and transfer it into a new sentence frame correctly.</p>',
      points: [
        'You need the correct information and the correct form.',
        'The answer usually comes from one short part of the passage.',
        'The completed sentence must be grammatical and faithful to the original meaning.',
      ],
    },
    {
      title: 'A Fast Strategy',
      content: '<p>Work through each sentence in this order:</p>',
      points: [
        'Read the incomplete sentence and predict the missing word type.',
        'Underline strong keywords such as names, dates, topics, and cause/effect language.',
        'Find the matching location in the passage and read slightly before and after it.',
        'Check whether the final answer fits grammatically after the words in the question stem.',
      ],
    },
    {
      title: 'Three Common Errors',
      content: '<p>Most sentence-completion mistakes come from these habits:</p>',
      table: {
        headers: ['Error', 'Why It Happens'],
        rows: [
          ['Wrong location', 'You find a similar keyword in the wrong paragraph'],
          ['Wrong form', 'You choose an idea from the passage but not the right grammatical form'],
          ['Overwriting', 'You add extra words that IELTS did not allow'],
        ],
      },
    },
    {
      title: 'Predict Before You Scan',
      content: '<p>Prediction saves time and improves accuracy.</p>',
      examples: [
        {
          label: 'Sentence stem',
          text: 'The committee delayed the launch because of ___ concerns.',
        },
        {
          label: 'Prediction',
          text: 'A noun describing a type of concern, perhaps safety, budget, or legal.',
        },
        {
          label: 'Benefit',
          text: 'You scan the passage more selectively because you know what kind of word you need.',
        },
      ],
    }
  ],
  exercises: [
    {
      title: 'Choose the Correct Completion',
      type: 'select',
      instructions: 'Select the option that best completes the sentence using the passage.',
      questions: [
        {
          text:
            'Passage: "The island was popular with biologists because several rare bird species nested there each spring."\nSentence: The island attracted biologists because rare bird species ___ there each spring.',
          options: [
            'nested',
            'escaped',
            'migrated away'
          ],
          answer: 'nested',
          explanation:
            'The passage directly states that several rare bird species nested there each spring.',
        },
        {
          text:
            'Passage: "Residents opposed the proposal because it would remove the last public garden in the district."\nSentence: Residents opposed the proposal because it would remove the district\'s last public ___ .',
          options: ['garden', 'meeting', 'bridge'],
          answer: 'garden',
          explanation:
            'The key noun "garden" transfers directly from the passage.',
        },
        {
          text:
            'Passage: "After the storm, engineers inspected the bridge before traffic was allowed to resume."\nSentence: Traffic was permitted to resume only after engineers had ___ the bridge.',
          options: ['inspected', 'designed', 'closed'],
          answer: 'inspected',
          explanation:
            'The passage establishes inspection as the condition before traffic resumed.',
        },
      ],
    },
    {
      title: 'Exact Word Transfer',
      type: 'fill-in',
      instructions: 'Type the missing word or short phrase from the passage.',
      questions: [
        {
          text:
            'Passage: "The report recommended shorter meetings to improve decision-making efficiency."\nSentence: The report said shorter meetings would improve decision-making ___ .',
          answer: 'efficiency',
          explanation:
            'The passage uses the exact phrase "decision-making efficiency".',
        },
        {
          text:
            'Passage: "Students with regular feedback made faster progress over the semester."\nSentence: Regular feedback helped students make faster ___ .',
          answer: 'progress',
          explanation:
            'The noun "progress" is the exact word used in the passage.',
        },
        {
          text:
            'Passage: "The museum introduced audio guides for visitors with visual impairments."\nSentence: Audio guides were introduced for visitors with visual ___ .',
          answer: 'impairments',
          explanation:
            'The answer transfers directly from the passage and fits the sentence.',
        },
      ],
    },
    {
      title: 'Grammar Fit Check',
      type: 'select',
      instructions: 'Choose the option that fits both meaning and grammar.',
      questions: [
        {
          text:
            'Passage: "The factory closure caused significant anxiety among local shop owners."\nSentence: Local shop owners experienced significant ___ after the factory closure.',
          options: ['anxiety', 'anxious', 'anxiously'],
          answer: 'anxiety',
          explanation:
            'The sentence needs a noun after "significant".',
        },
        {
          text:
            'Passage: "The town expanded rapidly once the railway was completed."\nSentence: The completion of the railway led to the town\'s rapid ___ .',
          options: ['expansion', 'expand', 'expanded'],
          answer: 'expansion',
          explanation:
            'The sentence stem requires a noun, and "expansion" preserves the meaning correctly.',
        },
      ],
    },
    {
      title: 'Explain the Evidence',
      type: 'writing',
      instructions: 'Explain why the selected answer is the best completion.',
      questions: [
        {
          text:
            'Passage: "The software update reduced loading times and improved battery life."\nSentence: The update improved battery ___ .\nWhy is your answer correct?',
          example:
            'The correct answer is "life" because the passage explicitly says "improved battery life", and that phrase fits the sentence exactly.',
          tip:
            'A strong explanation mentions both the source phrase and the grammar of the new sentence.',
        },
        {
          text:
            'Passage: "Most interviewees described the new timetable as more predictable."\nSentence: Most interviewees said the new timetable was more ___ .\nWhy is your answer correct?',
          example:
            'The answer is "predictable" because it is the adjective used in the passage and it completes the sentence naturally after "more".',
          tip:
            'Pay attention to whether the gap needs an adjective, noun, or verb.',
        },
      ],
    },
  ],
  tips: [
    'Predict the word class before you look back at the passage.',
    'Read around the gap to check grammar, not just content.',
    'Use the passage wording when possible instead of inventing synonyms.',
    'Treat word-limit instructions as part of the question, not an afterthought.',
  ],
  dailyPractice: [
    'Take 5 sentences from a reading passage and turn them into completion questions.',
    'Practice identifying whether a gap needs a noun, verb, adjective, or number.',
    'Review wrong answers and label the error: location, grammar, or word limit.',
    'Complete short passage-based gaps under timed conditions.',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext:
    'Sentence Completion: locating exact evidence in a passage, choosing the right grammatical form, and transferring answers accurately.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...sentenceCompletionReadingData,
      title: sentenceCompletionReadingData.title + ' (Foundations)',
      overview: { ...sentenceCompletionReadingData.overview, duration: '15-20 minutes' },
      sections: sentenceCompletionReadingData.sections.slice(0, 2),
      exercises: [
        {
          ...sentenceCompletionReadingData.exercises[0],
          title: 'Choose the Correct Completion (Basic)',
          questions: sentenceCompletionReadingData.exercises[0].questions.slice(0, 2),
        },
        {
          ...sentenceCompletionReadingData.exercises[1],
          title: 'Exact Word Transfer (Basic)',
          questions: sentenceCompletionReadingData.exercises[1].questions.slice(0, 2),
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...sentenceCompletionReadingData,
      title: sentenceCompletionReadingData.title + ' (Advanced)',
      overview: { ...sentenceCompletionReadingData.overview, duration: '30-35 minutes' },
      sections: [
        ...sentenceCompletionReadingData.sections,
        {
          title: 'Advanced Paraphrase Control',
          content:
            '<p>At higher levels, IELTS often hides the answer behind paraphrase. The keyword may move position, change form, or appear as part of a longer clause.</p>',
          points: [
            'Trace meaning, not just repeated vocabulary.',
            'Compare the grammar of the passage wording with the summary sentence.',
            'Reject answers that are semantically close but grammatically impossible.',
          ],
        },
      ],
      exercises: [
        ...sentenceCompletionReadingData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        {
          title: 'Advanced Completion Challenge',
          type: 'select',
          instructions: 'Choose the best completion when the passage uses paraphrase.',
          questions: [
            {
              text:
                'Passage: "The policy was eventually abandoned because enforcement costs were unsustainably high."\nSentence: The policy was abandoned because the costs of ___ were too high.',
              options: ['enforcement', 'eventual', 'abandonment'],
              answer: 'enforcement',
              explanation:
                'The sentence rewrites the idea, but the key noun remains "enforcement".',
            },
          ],
        },
      ],
      tips: [
        ...(sentenceCompletionReadingData.tips || []),
        'At Band 8+, ask whether your answer preserves the exact relationship in the sentence.',
        'When a passage is dense, map the clause around the answer before writing it.',
      ],
    };
  }
  return sentenceCompletionReadingData;
}
